import { FieldState } from 'support/types/ui';
import { DiffStatus } from 'components/DataTable/types';

type AISuggestedVariant = `ai-suggested-${DiffStatus}`;

export type CellVariant = 'default' | 'highlighted' | 'ai-edited' | AISuggestedVariant | 'warning' | 'error';

export type ViewCellVariant = CellVariant;

export type EditCellVariant = CellVariant | 'pending-validation';

type ViewCellVariantInputs = {
  fieldState: FieldState;
  diffStatus: DiffStatus | undefined;
  wasAIEdited: boolean;
  isHighlighted: boolean;
  // Lets a cell force the warning look outside the annotation system — e.g. an unassigned
  // transport unit, which is a structural state derived from form data, not a validation annotation.
  hasWarning?: boolean;
};

export const deriveViewCellVariant = ({
  fieldState,
  diffStatus,
  wasAIEdited,
  isHighlighted,
  hasWarning,
}: ViewCellVariantInputs): ViewCellVariant => {
  // FieldState can also be 'pending-validation', but view cells never see
  // pending fields (pending-validation is an edit-mode concept driven by RHF
  // dirtyFields). When it does come through, the branches below fall through
  // to 'default'/diff handling — the desired view-mode behavior.
  if (fieldState === 'error') {
    return 'error';
  }
  if (fieldState === 'warning' || hasWarning) {
    return 'warning';
  }
  if (diffStatus) {
    return `ai-suggested-${diffStatus}`;
  }
  if (wasAIEdited) {
    return 'ai-edited';
  }
  if (isHighlighted) {
    return 'highlighted';
  }
  return 'default';
};

type EditCellVariantInputs = {
  fieldState: FieldState;
  diffStatus: DiffStatus | undefined;
  isHighlighted: boolean;
  // See ViewCellVariantInputs.hasWarning.
  hasWarning?: boolean;
};

export const deriveEditCellVariant = ({
  fieldState,
  diffStatus,
  isHighlighted,
  hasWarning,
}: EditCellVariantInputs): EditCellVariant => {
  if (fieldState === 'pending-validation') {
    return 'pending-validation';
  }
  if (fieldState === 'warning' || hasWarning) {
    return 'warning';
  }
  if (fieldState === 'error') {
    return 'error';
  }
  if (diffStatus) {
    return `ai-suggested-${diffStatus}`;
  }
  if (isHighlighted) {
    return 'highlighted';
  }
  return 'default';
};
